import { useCallback } from 'react';
import uuid from 'uuid/v4';

import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { type FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { usePageContentId } from '@confluence/page-context';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { FORCE_TRANSITION_QUERY_PARAM } from '@confluence/named-routes/entry-points/force-transition.ts';
import { useShowConvertToLivePageFlag } from '@confluence/editor-flags/entry-points/useShowConvertToLivePageFlag.ts';
import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent.ts';

type ConvertToLivePageFlagProps = {
	flags: FlagsStateContainer;
};

export const ConvertToLivePageFlag = withFlags(({ flags }: ConvertToLivePageFlagProps) => {
	const [contentId] = usePageContentId();
	const { userId } = useSessionData();
	const { setQueryParams } = useRouteActions();
	const showConvertToLivePageFlag = useShowConvertToLivePageFlag({ flags });

	const onSubscriptionEvent = useCallback(
		(response: any) => {
			const { accountId, pageUpdated } = response || {};
			const trigger = pageUpdated?.trigger;

			if (trigger !== 'convertToLiveEdit') {
				return;
			}
			showConvertToLivePageFlag({ converterWasCurrentUser: accountId === userId });

			// When another user converts the page to a live doc, we need to add a query param to avoid the double action bar bug.
			// Without this, the page may read stale SSR values indicating it's not a live page, causing UI weirdness.
			// The query param forces RouteState to increment transitionId, preventing it from using initial SSR values.
			setQueryParams({ [FORCE_TRANSITION_QUERY_PARAM]: uuid() });
		},
		[showConvertToLivePageFlag, userId, setQueryParams],
	);

	useSubscriptionEvent({
		subscriberId: 'convertToLivePageFlag',
		contentId,
		delta: 'pageUpdated',
		contentType: 'page',
		skip: !contentId,
		onDataReceived: (response) => onSubscriptionEvent(response),
	});

	return null;
});
